import { useState, useCallback } from 'react';

/**
 * Hook reutilizable para el flujo de recorte de imágenes.
 * Devuelve el estado y las funciones necesarias para montar
 * un <Cropper> de react-easy-crop y obtener el blob final.
 */

const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y,
    pixelCrop.width, pixelCrop.height,
    0, 0,
    pixelCrop.width, pixelCrop.height
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/webp', 0.85);
  });
};

const useCropper = ({ onCropDone }) => {
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  /** Llama a esto al seleccionar un archivo de input */
  const openCropper = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const cancelCrop = () => {
    setShowCropper(false);
    setRawImageSrc(null);
  };

  const applyCrop = async () => {
    try {
      const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
      const previewUrl = URL.createObjectURL(blob);
      setShowCropper(false);
      setRawImageSrc(null);
      onCropDone({ blob, previewUrl });
    } catch {
      // propagar el error al componente
      throw new Error('No se pudo recortar la imagen.');
    }
  };

  return {
    rawImageSrc,
    showCropper,
    crop, setCrop,
    zoom, setZoom,
    onCropComplete,
    openCropper,
    cancelCrop,
    applyCrop,
  };
};

export default useCropper;
